import React, { useState, useEffect, useRef } from "react";
import { Button, Loading, Text } from '@nextui-org/react';
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import PhoneInput from "react-phone-input-2";
import FileUploadEhanced from "../../components/FileUploadEnhanced";
import {  warning as warnNotif } from "../../lib/userAlerts";

import {
  snakeCaseObj,
} from "../../lib/utils";

import {
  fetchWithJson,
  VALID_EMAIL_REGEX,
} from "../../lib/api";
import { updateTitle } from "../../lib/utils";

// --- Label and SectionHeader components ---
const RequireLabel = ({ lblName }) => (
  <div className="label">
    <span className="label-text">{lblName} <span className="fw8" style={{ color: 'red' }}>*</span></span>
  </div>
);

const RegularLabel = ({ lblName }) => (
  <div className="label">
    <span className="label-text">{lblName}</span>
  </div>
);

const SectionHeader = ({ dispText }) => (
  <div className="f4 ttu fw4 gray-600 col-span-3">{dispText}</div>
);

const AccountDetails = ({
  initialDetails,
  setUrlCB,
  onSave,
  uploadCb,
  logoUrl,
  saveBtnNm
}) => {
  // --- State ---
  const [state, setState] = useState({
    loading: false,
    isActive: true,
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    primaryContact: "",
    email: "",
    email_free: true,
    phoneNo: "",
    logoUrl: false,
    companyUniqueId: "",
    useEmployeesDriver: 1,
    isBroker: 1,
    scacNumber: "",
    dotNumber: "",
    mcNumber: ""
  });

  const formRef = useRef(null);

  // --- Effects ---
  useEffect(() => {
    if (initialDetails) handleInitialState(initialDetails);
    if (setUrlCB) setUrlCB((logoUrl) => setState(s => ({ ...s, logoUrl })));

    updateTitle("Account Management");
    // eslint-disable-next-line
  }, [initialDetails, setUrlCB]);

  // --- Handlers ---
  const setField = (field, value) => setState(s => ({ ...s, [field]: value }));

  const setActive = () => setField("isActive", !state.isActive);

  const setGAddress = (address) => {
    setField("address", address);
    geocodeByAddress(address)
      .then((results) => {
        const address_components = results[0].address_components;
        const cityObj = address_components.find(a => a.types.includes("locality"));
        const countryObj = address_components.find(a => a.types.includes("country"));
        const zipObj = address_components.find(a => a.types.includes("postal_code"));
        const stateObj = address_components.find(a => a.types.includes("administrative_area_level_1"));
        setState(s => ({
          ...s,
          country: countryObj ? countryObj.long_name : s.country,
          city: cityObj ? cityObj.long_name : "Not Available",
          zipCode: zipObj ? zipObj.long_name : "Not Available",
          state: stateObj ? stateObj.long_name : "Not Available",
        }));
      })
      .catch(() => {});
  };

  const setZIP = (e) => {
    const zipCode = e.target.value;
    if (/^\d*$/.test(zipCode)) setField("zipCode", zipCode);
  };

  const setPhoneNumber = (phone) => setField("phoneNo", phone);

  const handleSave = async () => {
    if (formRef.current) {
      formRef.current.reportValidity();
      if (formRef.current.checkValidity() === false) return;
    }
    if (!VALID_EMAIL_REGEX.test(state.email)) {
      return warnNotif("Please enter a valid email");
    }
    if (state.zipCode.length !== 5) {
      return warnNotif("Please enter a valid Zipcode");
    }
    setField("loading", true);
    let info = snakeCaseObj(state);
    delete info.loading;
    try {
      if (onSave) {
        if ((await onSave(info)) === false) {
          setField("loading", false);
        }
      }
    } catch (ex) {
      setField("loading", false);
    }
  };

  const handleInitialState = (d) => {
    setState(s => ({
      ...s,
      isActive: d.client_active === 1,
      name: d.name,
      address: d.address,
      city: d.city,
      state: d.state,
      zipCode: d.zip,
      country: d.country,
      primaryContact: d.primary_contact,
      email: d.email,
      phoneNo: d.phone_no,
      loading: d.loading || s.loading,
      companyUniqueId: d.companyUniqueId,
      useEmployeesDriver: d.use_employees_driver,
      isBroker: d.is_broker,
      scacNumber: d.scac_code,
      dotNumber: d.dot_number,
      mcNumber: d.mc_number
    }));
  };

  // --- Renderers ---
  const renderCompanyDetails = () => (
    <div className="grid grid-cols-3 grid-cols-1 gap-4">
      <div className="col-span-3">
        <RequireLabel lblName="Company Name" />
        <input
          onChange={e => setField("name", e.target.value)}
          value={state.name}
          type="text"
          id='companyName'
          name='companyName'
          className="h-10 border rounded w-full px-4"
          autoComplete="off"
          required
        />
      </div>
      <div className="col-span-1">
        <RequireLabel lblName="SCAC Code" />
        <input
          required
          type="text"
          name="scacNumber"
          id="scacNumber"
          className="h-10 border rounded w-full px-4"
          onChange={e => setField("scacNumber", e.target.value)}
          value={state.scacNumber}
          autoComplete="off"
          placeholder=""
        />
      </div>
      <div className="col-span-1">
        <RegularLabel lblName="DOT Number" />
        <input
          required
          type="text"
          name="dotNumber"
          id="dotNumber"
          className="h-10 border rounded w-full px-4"
          onChange={e => setField("dotNumber", e.target.value)}
          value={state.dotNumber}
          autoComplete="off"
          placeholder=""
        />
      </div>
      <div className="col-span-1">
        <RegularLabel lblName="MC Number" />
        <input
          required
          type="text"
          name="mcNumber"
          id="mcNumber"
          className="h-10 border rounded w-full px-4"
          onChange={e => setField("mcNumber", e.target.value)}
          value={state.mcNumber}
          autoComplete="off"
          placeholder=""
        />
      </div>
      <div className="form-control col-span-1 mt-6">
        <label className="cursor-pointer label">
          <span className="text-lg">Company Is Active</span>
          <input type="checkbox" className="toggle toggle-success" checked={state.isActive} onChange={setActive} />
        </label>
      </div>
      <div role="alert" className="alert col-span-2 mt-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        <span>Disabling this toggle will close your account.</span>
      </div>
    </div>
  );

  const renderContactDetails = () => (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-3">
        <RequireLabel lblName="Primary Contact" />
        <input
          type="text"
          name="full_name"
          id="full_name"
          className="h-10 border mt-1 rounded px-4 w-full"
          onChange={e => setField("primaryContact", e.target.value)}
          value={state.primaryContact}
          required
        />
      </div>
      <div className="col-span-2">
        <RequireLabel lblName="Email Address" />
        <div className="gray-600 w-100">
          <input
            disabled
            aria-disabled
            value={state.email}
            type="email"
            name="email"
            className='h-10 border mt-1 rounded px-4 w-full bg-gray-100'
            required
          />
        </div>
      </div>
      <div className="col-span-1">
        <RegularLabel lblName="Phone Number" />
        <PhoneInput
          containerClass="transition-all flex items-center h-10 border mt-1 rounded w-full"
          country={"us"}
          prefix={'+'}
          inputStyle={{ height: '100%', width: '100%' }}
          preferredCountries={["us", "in"]}
          value={state.phoneNo}
          onChange={setPhoneNumber}
        />
      </div>
    </div>
  );

  const renderAddressDetail = () => {
    const placeOption = {
      types: ["address"],
      componentRestrictions: { country: "us" },
    };
    const placeInputProps = {
      value: state.address,
      onChange: setGAddress,
      required: true,
    };
    const placeCssClasses = {
      root: "w-100",
      input: "h-10 border mt-1 rounded px-4 w-full",
      autocompleteContainer: "ba bw1 rounded",
    };
    return (
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-3">
          <RequireLabel lblName="Address" />
          <PlacesAutocomplete
            classNames={placeCssClasses}
            inputProps={placeInputProps}
            debounce={500}
            options={placeOption}
          />
        </div>
        <div className="col-span-1">
          <RegularLabel lblName="City" />
          <input
            required
            type="text"
            name="city"
            id="city"
            className="h-10 border mt-1 rounded px-4 w-full"
            onChange={e => setField("city", e.target.value)}
            value={state.city}
          />
        </div>
        <div className="col-span-1">
          <RequireLabel lblName="Zip Code" />
          <input
            onChange={setZIP}
            value={state.zipCode}
            type="text"
            name="zipcode"
            id="zipcode"
            className="transition-all flex items-center h-10 border mt-1 rounded px-4 w-full"
            placeholder=""
            required
          />
        </div>
        <div className="col-span-1">
          <RequireLabel lblName="Country" />
          <select
            onChange={e => setField("country", e.target.value)}
            value={state.country}
            className="select select-bordered select-sm h-10 mt-1 rounded  w-full">
            <option disabled>Select Country</option>
            <option>USA</option>
            <option>India</option>
          </select>
        </div>
      </div>
    );
  };

  // --- Render ---
  return (
    <form
      ref={formRef}
      className="flex justify-between px-32 py-8 grid grid-cols-3 gap-12 bg-gray-100"
    >
      <div className="xl:col-span-2 md:col-span-3 space-y-6 bg-white p-8 shadow-lg">
        <div className="collapse collapse-arrow">
          <input type="radio" name="my-accordion-2" defaultChecked />
          <div className="collapse-title">
            <SectionHeader dispText="Company Info" />
          </div>
          <div className="collapse-content">
            {renderCompanyDetails()}
          </div>
        </div>
        <div className="collapse collapse-arrow">
          <input type="radio" name="my-accordion-2" />
          <div className="collapse-title">
            <SectionHeader dispText="Contact Details" />
          </div>
          <div className="collapse-content">
            {renderContactDetails()}
          </div>
        </div>
        <div className="collapse collapse-arrow">
          <input type="radio" name="my-accordion-2" />
          <div className="collapse-title">
            <SectionHeader dispText="Address details" />
          </div>
          <div className="collapse-content">
            {renderAddressDetail()}
          </div>
        </div>
        <div className="py-8 flex justify-end border-t">
          <Button
            auto
            css={{ br: 0 }}
            onClick={handleSave}
            loading={state.loading}
          >
            {state.loading ? <Loading type='points' color='primary' /> : <Text color='currentColor'>{saveBtnNm}</Text>}
          </Button>
        </div>
      </div>
      <div className="xl:col-span-1 md:col-span-3 p-6 bg-white shadow-lg">
        <div className="card p-4 glass">
          <figure><img src={state.logoUrl || logoUrl} alt="your logo!" /></figure>
          <div className="card-body">
            <h3 className="card-title">Change your logo?</h3>
            <div className="card-actions justify-start">
              <FileUploadEhanced
                defaultName={"Choose your new logo .. "}
                uploadCb={uploadCb}
                email={state.email}
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AccountDetails;